import { useAuthMe } from '@h2hspark/auth-ui';
import { ApiRequestError, Button, MemberEventView, Skeleton } from '@h2hspark/ui';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { useMyMeetList } from '../attendee-preview/api';
import { GoingPill } from '../attendee-preview/going-count-panel';
import { MeetListPanel } from '../attendee-preview/meet-list-panel';
import { WhosComingPanel } from '../attendee-preview/whos-coming-panel';
import { EventPricePanel } from '../event-orders/event-price-panel';
import { ReserveActionBar } from '../event-orders/reserve-action-panel';
import { useMyRegistration } from '../event-registrations/api';
import { PastEventPanel } from '../event-registrations/past-event-panel';
import { MatchAlert, RegisteredEventHeader } from '../event-registrations/registered-event-header-card';
import { RegistrationPanel } from '../event-registrations/registration-panel';
import { hostProfilePath } from '../organizer-profile/constants';

import { rememberPublicEvent, usePublicEvent } from './api';
import {
  EVENT_UNAVAILABLE_COPY,
  isRetryableEventFailure,
  readableEventLocation,
} from './constants';
import type { PublicEventDetail } from './types';

/**
 * The event detail page (`MEM-015`, `MEM-016`, `MEM-017`), public (`MEM-019` AC-1, AC-6).
 *
 * ## Built from `Web App.dc.html` (10 September), Event detail, lines 263–339
 *
 * The prototype draws two pages on one route, and so does this:
 *
 *   - **Not registered** — a two-column page, `1.7fr 1fr`. On the left the cover, the 72px date
 *     tile hero (atmosphere and *N going* pills, the name, the long date), *Hosted by*, the
 *     description, the tags and the location card; on the right a sticky sidebar with the price
 *     per seat, *Reserve my spot*, and Who's coming locked behind the reservation.
 *   - **Registered** — a full-width event header card (date tile, name, date and city, *View your
 *     ticket*, Requests while there are any, the status pill, and the Before · During · After
 *     stepper), then the same left column without the hero, the *We found N matches* alert, and a
 *     sidebar carrying the meet list and Who's coming.
 *
 * On a phone it is one column in that order (`MEM-056` AC-2), the sidebar inline after the
 * location, and *Reserve my spot* pinned to the foot of the screen (`MEM-056` AC-4, `MEM-020`
 * AC-1). The PR lists every place it departs from the prototype, and why.
 *
 * ## Which page a reader gets
 *
 * **Registered** is a confirmed registration, answered by the server (`MEM-017` AC-5), on an event
 * that has not been cancelled. A cancelled or refunded registration answers `null` and gets the
 * unregistered page (`MEM-017` AC-6, `MEM-053` AC-5). The registration is read before the page is
 * drawn, so a member who is going is not shown the reservation page for a moment first.
 *
 * ## What is here that the prototype does not draw
 *
 * **The interest tag chips** are the Discover card's, not the prototype's `#hashtags`; **the
 * refund terms** (`EVT-024` AC-2) and **the confirmed panel** (`MEM-017` AC-1) are the
 * specification's and stay, the panel above the location card; **the unlisted notice** is
 * `MEM-019` AC-6's.
 */
/**
 * Once the event is known, put its readable address in the address bar (`EVT-041` AC-5): replaced,
 * not pushed, so Back leaves the page rather than returning to the id form of it. The event is
 * filed under the new address first, so the swap draws what is already on screen.
 */
function useReadableAddress(eventRef: string, detail: PublicEventDetail | undefined): void {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  React.useEffect(() => {
    if (detail === undefined) return;
    const readable = readableEventLocation({
      ref: eventRef,
      detail,
      search: location.search,
      hash: location.hash,
    });
    if (readable === null || !detail.slug) return;
    rememberPublicEvent(queryClient, detail.slug, detail);
    navigate(readable, { replace: true });
  }, [detail, eventRef, location.search, location.hash, navigate, queryClient]);
}

export function EventDetailPage(): React.ReactElement {
  /* The route's `:eventId` holds the readable address or the id (`EVT-041` AC-4/AC-5). The lookup
     takes either; everything the member reads about their own place at the event takes the id the
     lookup answered with. */
  const { eventId: eventRef } = useParams<{ eventId: string }>();
  const query = usePublicEvent(eventRef ?? '');
  const eventId = query.data?.eventId ?? '';
  useReadableAddress(eventRef ?? '', query.data);
  const reader = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { user, loading } = useAuthMe();
  const signedIn = user !== null;
  const registrationQuery = useMyRegistration(eventId, { enabled: signedIn });
  /* **Read here, not only inside the module, so the page can decide whether there is a sidebar at
     all.** On a past event Who's coming is gone (Sumit's ruling 27) and the meet list may be empty,
     and a page that passed the slot anyway kept the prototype's `1.7fr 1fr` grid with nothing in
     the right column — which is what Sumit asked about: *"for closed event should be expand the row
     to full width?"* (ruling 31). `MeetListPanel` still decides for itself whether to draw; this
     decides whether there is a column for it to draw in. One query key, so the two reads are one
     request. */
  const meetListQuery = useMyMeetList(eventId, { enabled: signedIn });

  const waitingForReader = (loading && user === null) || (signedIn && registrationQuery.isPending);
  if (query.isPending || waitingForReader) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[180px] w-full rounded-xl" />
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  /*
    **Only when there is no event to draw** — TW 44108653
    (https://ten243.teamwork.com/app/tasks/44108653). This was `query.error !== null || …`, and a
    query that fails a *background* re-read keeps the event it already had and carries the error
    beside it. So a member arriving from checkout — the event in the cache and stale — lost a page
    the app could draw to one failed request, and read *"We could not open that event"* about an
    event it was holding. The event it has is still the event; the next re-read corrects it.
  */
  if (query.data === undefined) {
    const notFound = query.error instanceof ApiRequestError && query.error.statusCode === 404;
    const retryable = !notFound && isRetryableEventFailure(query.error);
    return (
      <div className="space-y-4">
        <BackToDiscover />
        <h1 className="text-2xl">
          {notFound ? EVENT_UNAVAILABLE_COPY.notFoundHeading : EVENT_UNAVAILABLE_COPY.heading}
        </h1>
        <p className="text-sm text-muted-foreground">
          {notFound
            ? EVENT_UNAVAILABLE_COPY.notFoundBody
            : retryable
              ? EVENT_UNAVAILABLE_COPY.retryableBody
              : EVENT_UNAVAILABLE_COPY.finalBody}
        </p>
        {/* **Try again only where it can work.** A dropped connection, a timeout, throttling or
            a server error may answer differently a second later; a refusal will not, and a
            control that can only fail again is worse than none. */}
        {retryable ? (
          <Button
            variant="outline"
            disabled={query.isFetching}
            onClick={() => {
              void query.refetch();
            }}
          >
            {EVENT_UNAVAILABLE_COPY.retry}
          </Button>
        ) : null}
      </div>
    );
  }

  const detail = query.data;
  const profilePath = hostProfilePath(detail);
  /* **`MEM-053` AC-1 and AC-5.** The header card and the stepper are a confirmed registrant's, and
     only on an event that is still an event: a cancelled one is answered by its notice, and its
     registrations are refunded (`EVT-013`). */
  const registration = signedIn ? (registrationQuery.data?.registration ?? null) : null;
  const registered = registration !== null && detail.lifecycleState !== 'cancelled';
  /* The reservation sidebar is for an event still taking reservations — the **same** server-side
     `openForRegistration` that gates the registration region, and never for an evening that has
     already happened (`MEM-015` AC-7). */
  const reservable = !detail.hasEnded && detail.openForRegistration;
  const hasMeetListEntries = (meetListQuery.data?.meetList?.entries.length ?? 0) > 0;

  /*
    **This page is `MemberEventView`, and that is `EVT-010` AC-4 seen from the member's side.**

    The page's markup moved into `@h2hspark/ui` on 2026-08-30 so the organizer's Review step could
    render *the same component* rather than a second rendering of it — Sumit's ruling, on the
    reasoning that two renderings kept in step by hand will drift, and the screen that says
    "exactly what a member sees" is the worst place for that to happen.

    What is left here is the regions that are about the **reader** rather than about the event,
    and they go in as slots so the component keeps the prototype's order rather than each caller
    composing its own.
  */
  return (
    <MemberEventView
      event={detail}
      readerTimeZone={reader}
      hostProfileHref={profilePath}
      renderHostLink={({ href, children }) => (
        // `Link`, not an `<a href>`: this is an in-app route and an anchor would reload the SPA.
        // The component decides *whether* there is a link — it is `null` without a slug — and this
        // app decides what a link is in it.
        <Link to={href} className="font-bold text-brand underline underline-offset-2">
          {children}
        </Link>
      )}
      slots={{
        back: <BackToDiscover />,
        header:
          registered && registration !== null ? (
            <RegisteredEventHeader
              event={detail}
              confirmationCode={registration.confirmationCode}
            />
          ) : undefined,
        heroPills: <GoingPill eventId={detail.eventId} />,
        alert: registered ? (
          <MatchAlert eventId={detail.eventId} hasEnded={detail.hasEnded} />
        ) : undefined,
        /* **`EVT-013` AC-5's second half — "the register action is removed" — is this condition.**
           `openForRegistration` is the server's answer rather than an inference from
           `lifecycleState`, so the rule lives where `MEM-016` AC-6 puts the address rule and for
           the same reason. The whole region goes rather than the button alone: a confirmed
           member's panel offers a ticket and a calendar entry for an event that is not happening,
           which is worse than the button. What replaces it is the lifecycle notice. */
        /* **`MEM-015` AC-7 is the first branch, and it comes before the lifecycle one.**

           `hasEnded` and `openForRegistration` answer different questions and a past event
           satisfies both: `openForRegistration` is `lifecycleState === 'published'` and says
           nothing about the clock, so before this branch existed an evening that finished last
           week kept the whole live page — a Cancel control for a used place, an Add to calendar
           for a night that happened, and a Connect pill for a surface the server had closed.

           `PastEventPanel` is that page in the past tense. It keeps the ticket (Sumit's ruling),
           the matches (a snapshot, `MEM-026`) and the follow-up, and says in a sentence what
           replaced the connect code rather than drawing a dead one (`MEM-031` AC-7).

           `hasEnded` is `false` for a **cancelled** event however long ago its instant was, so a
           cancelled event still falls to the branch below and is answered by `lifecycleNotice`.
           It did not happen; there is nothing to look back on. */
        registration: detail.hasEnded ? (
          <PastEventPanel eventId={detail.eventId} />
        ) : detail.openForRegistration ? (
          <RegistrationPanel eventId={detail.eventId} />
        ) : undefined,
        price:
          !registered && reservable ? <EventPricePanel ticketTypes={detail.ticketTypes} /> : undefined,
        /* [MEM-020-AC-1] The reserve action: pinned at the foot of the screen on a phone, and in the
           sidebar from `md` (`MEM-056` AC-4) — one element, see `ReserveActionBar`.

           Gated on the **same** `openForRegistration` the registration region is, for the reason
           `MEM-017` AC-3 gives. The bar withholds itself for a member who already holds a place —
           see its header for why that is answered there rather than here.

           [MEM-048-AC-9] The **whole** list, not `ticketTypes[0]`. Handing the bar the first row
           is what made a three-tier event read as a one-price event.

           [MEM-015-AC-7] And **not on an event that has already happened**, which
           `openForRegistration` alone does not exclude: it is the lifecycle state and knows
           nothing about the clock, so a published evening that finished last week carried a
           pinned "Reserve my spot · $25" bar over a page about the past. */
        pinnedAction:
          !registered && reservable ? (
            <ReserveActionBar eventId={detail.eventId} ticketTypes={detail.ticketTypes} />
          ) : undefined,
        /* Who's coming, in the sidebar. For a registrant, the meet list above it (`MEM-013` AC-4);
           for anybody else on an event still taking reservations, the locked row beneath the
           Reserve action. `MEM-011` AC-3's module is a separate authenticated call rather than a
           field on this page's payload, and deliberately: this event payload is **public**, and a
           roster growing onto it would be the member-data boundary moving by accident. The server
           decides who gets the list (Sumit's ruling of 2026-09-11); see `WhosComingPanel`. */
        /* **Who's coming closes with the evening** — Sumit, 2026-09-11: on a past event the module
           drew a heading and *"N going"* with nothing under it.

           Two fixes were possible and this is the one chosen: the module **goes** once the event
           is over, rather than being refilled with the people the member actually met.

           - `MEM-015` AC-7 says a past event *"offers only what outlives it — the ticket, the
             matches and the follow-up"*, and Who's coming is none of the three. It is a plan for an
             evening, and the evening happened.
           - The people the member **did** meet are already somewhere, twice: on the follow-up,
             which the stepper's *After · Follow up now* leads to, and in their connections. Putting
             them in the sidebar too would be a third rendering of one list, and the first that had
             to decide what "met" means (`MEM-030` AC-6's logged connections? `MEM-032`'s accepted
             requests? both?) — a decision no criterion has made.
           - And it cannot simply keep showing the roster: Who's coming follows each attendee's own
             election (`MEM-PRV-008`), which they made about being visible to people deciding
             whether to come. Reading it back weeks later is not what they agreed to.

           The **meet list** stays, because `MEM-013` AC-4 puts it on the event's screen without
           qualifying by time, and it is the member's own record rather than anybody else's
           election. The empty-roster sentence added alongside this fixes the same defect for a live
           event, where it is reachable whenever every registrant has kept their profile private. */
        attendance: registered
          ? detail.hasEnded
            ? // Once the evening is over the only thing left for the sidebar is the member's own
              // meet list, and only if they saved somebody. With nothing to put there the slot is
              // not passed at all, so the body takes the full content width (ruling 31).
              hasMeetListEntries
              ? <MeetListPanel eventId={detail.eventId} />
              : undefined
            : (
                <>
                  <MeetListPanel eventId={detail.eventId} />
                  <WhosComingPanel eventId={detail.eventId} />
                </>
              )
          : reservable ? (
            <WhosComingPanel eventId={detail.eventId} divided />
          ) : undefined,
      }}
    />
  );
}

/**
 * The prototype's back link (§3.6): transparent, `--text-muted`, `700/var(--text-sm)`, a leading
 * `arrow-left`. Its event page draws none on the desktop, where the header's Discover item does
 * the job; the phone design has a back control on the event hero, and one link at every width is
 * the same way back for both.
 */
function BackToDiscover(): React.ReactElement {
  return (
    <PageBackLink to="/app">
      <span>Discover</span>
    </PageBackLink>
  );
}
